// ============================================================
// DateInput.tsx
// Three-box DD / MM / YYYY date input. Replaces the native
// <input type="date"> calendar picker per solicitor preference.
//
// Storage contract: the parent receives an ISO "YYYY-MM-DD" string when
// all three boxes form a valid real calendar date, and null ONLY when all
// three boxes are emptied on purpose (a deliberate clear).
// ★ A HALF-TYPED or impossible date (e.g. "12 / 0_ / 2024", 31/02/2024,
// a year out of range) is NEVER sent — not as null, not as anything. The
// parent keeps its old value (so an autosave can't wipe a saved date) and
// the field shows "Finish the date or clear it" once focus leaves it.
// onValidityChange(false) tells explicit-save forms to hold their Save.
//
// UX:
//   - inputMode="numeric" + digits-only filter (mobile keyboards
//     surface the number pad; paste of "11/01/2002" gets stripped
//     to "11012002" then truncated to "11" in the day box —
//     pasting across boxes is deferred to v2).
//   - Auto-advance: 2 digits in DD → MM, 2 digits in MM → YYYY.
//     YYYY has no auto-advance (tabbing leaves the component).
//   - Backspace on empty MM → focus DD; on empty YYYY → focus MM.
//     Backspace on empty DD stays (nothing to fall back to).
//   - External value changes (e.g. parent reset, fresh case load)
//     resync local state via useEffect on the value prop.
//
// Stored values: the boxes SHOW any date the app stores — "YYYY-MM-DD", an
// ISO timestamp or the legacy UK "D/M/YYYY" (utils/civilDate textDateToIso) —
// and a stored date outside the TYPING year range (e.g. a future year on a
// past-only field, or before 1900) is shown as-is, never flagged "Finish the
// date": the range only polices what's typed. A stored value the widget can't
// read at all is never cleared by a stray keystroke (see commit()).
//
// ★ FUTURE dates (B2 polish): on a past-only field (no allowFuture) a
// COMPLETE, real date after today (London) is never sent either — it shows
// "Can't be in the future" (at once: the date is finished, so it's not a
// nag) and onValidityChange(false) holds an explicit Save. It used to be
// "Finish the date or clear it" for a later year, or passed on (and refused
// by the server) for later this year.
//
// Validity:
//   - Day 1–31, month 1–12, year 1900..currentYear+100; without allowFuture
//     the date must also be on or before today.
//   - Real-calendar check via Date.UTC + round-trip compare so
//     Feb 30, Apr 31, and Feb 29 in non-leap years all return null.
//   - Out-of-range / half-typed digits don't block typing — nothing is
//     sent to the parent until the boxes form a real date (or are emptied).
// ============================================================
import React, { useState, useEffect, useRef } from 'react'
import { londonToday, textDateToIso } from '../../utils/civilDate'

interface DateInputProps {
  value: string | null
  // onChange fires on EVERY keystroke that changes the parts, passing
  // toISOIfValid(dd, mm, yyyy) — null while incomplete/invalid, ISO
  // string when the three boxes form a valid date. Kept optional
  // (was required pre-3-July) so callers can opt into commit-on-blur
  // mode via `onCommit` without also having to pass a no-op onChange.
  // When onCommit is provided, onChange is IGNORED (see below).
  onChange?: (value: string | null) => void
  // Commit-on-blur opt-in. When provided, the component enters commit
  // mode: local state (dd/mm/yyyy) holds the displayed value as the
  // user types and backspaces, and the parent is NOT notified per-
  // keystroke. onCommit fires ONLY when focus leaves the widget as a
  // whole (i.e. not on internal Tab hops between DD ↔ MM ↔ YYYY),
  // passing toISOIfValid(dd, mm, yyyy) — same value shape onChange
  // would have delivered. Fixes the "backspace a valid date empties
  // the store mid-edit and unmounts value-gated renders" trap that
  // hit AutomaticSection's handover / openDate / closeDate fields
  // (see AutomaticSection.tsx — the three DateInputs there use this
  // mode). Other DateInput consumers that omit onCommit get the
  // original per-keystroke behaviour byte-for-byte.
  onCommit?: (value: string | null) => void
  className?: string
  fieldClassName?: string
  // Opt-in for FUTURE-date fields (e.g. deadline due dates: limitation /
  // trial / court dates, which are inherently in the future). Default false
  // keeps a field past-only (DOB, accident date, close date…): a complete date
  // after today shows "Can't be in the future" and is never sent. When true,
  // any real date up to thisYear+100 is sent (year 9999 still rejects). The
  // 1900 lower bound and day/month validity are the same either way.
  allowFuture?: boolean
  // Accessible name of the WHOLE date (e.g. "Date of birth", "Date of
  // accident"). The three boxes are announced as "<label> — day / month /
  // year" so a screen reader never hears three anonymous "Day/Month/Year"
  // boxes that could be any date. Presentation only — no behaviour change.
  ariaLabel?: string
  // Told when the boxes hold a half-typed / impossible date (false) and
  // when they're empty or a complete real date again (true) — so a form
  // with an explicit Save can hold it. Fires only on a change of state.
  onValidityChange?: (valid: boolean) => void
  // (Audit fix batch 2, Low 14) A form's own error for this date: its element
  // id (linked to the three boxes) and whether to mark them invalid.
  describedBy?: string
  invalid?: boolean
}

// The state of the three boxes: all empty (a deliberate clear), a complete
// real date, or something in between (partial / impossible / out of range).
// 'future' = a complete real date after today on a past-only field.
export type DatePartsState = { kind: 'empty' } | { kind: 'valid'; iso: string } | { kind: 'future'; iso: string } | { kind: 'invalid' }
export const FINISH_DATE_MESSAGE = 'Finish the date or clear it'
export const FUTURE_DATE_MESSAGE = 'Can’t be in the future'

// Parse a stored date → {dd, mm, yyyy}: "YYYY-MM-DD", an ISO timestamp or the
// legacy "D/M/YYYY". Tolerant of null and of anything unreadable — both yield
// empty parts.
const parseISO = (raw: string | null): { dd: string; mm: string; yyyy: string } => {
  const iso = textDateToIso(raw)
  if (!iso) return { dd: '', mm: '', yyyy: '' }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return { dd: '', mm: '', yyyy: '' }
  return { yyyy: m[1], mm: m[2], dd: m[3] }
}

// Round-trip via Date.UTC so impossible dates (Feb 30 etc.) fail.
const toISOIfValid = (dd: string, mm: string, yyyy: string): string | null => {
  if (dd.length !== 2 || mm.length !== 2 || yyyy.length !== 4) return null
  const d = Number(dd)
  const mo = Number(mm)
  const y = Number(yyyy)
  if (!Number.isInteger(d) || !Number.isInteger(mo) || !Number.isInteger(y)) return null
  if (mo < 1 || mo > 12) return null
  if (d < 1 || d > 31) return null
  const thisYear = new Date().getFullYear()
  // Upper year bound: thisYear+100 (absurd typos like 9999 still reject). A
  // past-only field's FUTURE date is a real date here — datePartsState then
  // calls it 'future' (its own message) instead of "unfinished".
  const maxYear = thisYear + 100
  if (y < 1900 || y > maxYear) return null
  const dt = new Date(Date.UTC(y, mo - 1, d))
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null
  return `${yyyy}-${mm}-${dd}`
}

export const datePartsState = (dd: string, mm: string, yyyy: string, allowFuture = false, today: string = londonToday()): DatePartsState => {
  if (dd === '' && mm === '' && yyyy === '') return { kind: 'empty' }
  const iso = toISOIfValid(dd, mm, yyyy)
  if (!iso) return { kind: 'invalid' }
  return !allowFuture && iso > today ? { kind: 'future', iso } : { kind: 'valid', iso }
}

const digitsOnly = (s: string, max: number): string => s.replace(/\D/g, '').slice(0, max)

const DateInput: React.FC<DateInputProps> = ({ value, onChange, onCommit, className, fieldClassName, allowFuture = false, ariaLabel, onValidityChange, describedBy, invalid }) => {
  const [dd, setDd] = useState('')
  const [mm, setMm] = useState('')
  const [yyyy, setYyyy] = useState('')
  // Focus is inside the widget (the "finish the date" message waits until
  // the user leaves it, so it doesn't nag mid-typing).
  const [focused, setFocused] = useState(false)
  const lastValid = useRef(true)
  // The boxes still show the STORED value (no keystroke since it loaded) — a
  // stored date is never flagged, even outside the typing year range.
  const [showingStored, setShowingStored] = useState(true)

  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  // Mode gate. Presence of onCommit puts us in commit-on-blur mode
  // (keystroke never notifies parent; blur out of the widget does).
  // Absence keeps the original per-keystroke onChange behaviour.
  const isCommitMode = onCommit !== undefined

  // Resync from parent. Only overwrite local state when the prop
  // describes a different date than what we're rendering — avoids
  // clobbering mid-typing partial state on every parent re-render.
  //
  // In commit-on-blur mode the parent's value only advances after
  // blur, so during a mid-typing burst local and prop legitimately
  // disagree; the short-circuit `if (localISO === value) return`
  // prevents the effect from clobbering the user's fresh partial
  // state on unrelated parent re-renders (React's dep-array
  // identity check on [value] already handles the same-prop-object
  // case, but this belt-and-braces both).
  useEffect(() => {
    const localISO = toISOIfValid(dd, mm, yyyy)
    if (localISO !== null && localISO === value) return
    const parts = parseISO(value)
    // The same date already on screen (a legacy "12/03/2025" shape, or a
    // stored year outside the typing range) → nothing to resync.
    if ((dd || mm || yyyy) && parts.dd === dd && parts.mm === mm && parts.yyyy === yyyy) return
    setDd(parts.dd)
    setMm(parts.mm)
    setYyyy(parts.yyyy)
    setShowingStored(true)
    if (!lastValid.current) { lastValid.current = true; onValidityChange?.(true) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Per-keystroke commit — only used in the pre-3-July path (no
  // onCommit provided). In commit-mode this is a no-op; the parent
  // hears about the value once on blur.
  const commit = (nd: string, nm: string, ny: string): void => {
    setShowingStored(false)
    const st = datePartsState(nd, nm, ny, allowFuture)
    const valid = st.kind === 'valid' || st.kind === 'empty'
    if (valid !== lastValid.current) { lastValid.current = valid; onValidityChange?.(valid) }
    if (isCommitMode) return
    // Half-typed / impossible / a future date on a past-only field → tell the
    // parent NOTHING (its value stays) — the field says why.
    if (!valid) return
    // Emptied boxes over a stored value this widget can't read (they showed
    // empty from the start — free text) → not a deliberate clear of a date the
    // person could see; a typed-then-deleted digit mustn't wipe it.
    if (st.kind === 'empty' && value !== null && value !== '' && textDateToIso(value) === null) return
    onChange?.(st.kind === 'valid' ? st.iso : null)
  }

  const onDay = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = digitsOnly(e.target.value, 2)
    setDd(v)
    commit(v, mm, yyyy)
    if (v.length === 2) monthRef.current?.focus()
  }
  const onMonth = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = digitsOnly(e.target.value, 2)
    setMm(v)
    commit(dd, v, yyyy)
    if (v.length === 2) yearRef.current?.focus()
  }
  const onYear = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = digitsOnly(e.target.value, 4)
    setYyyy(v)
    commit(dd, mm, v)
  }

  // Backspace-on-empty hop back. Don't preventDefault — we want
  // the keystroke to still do its normal nothing-to-delete thing.
  const onMonthKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && mm === '') dayRef.current?.focus()
  }
  const onYearKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && yyyy === '') monthRef.current?.focus()
  }

  // Shared blur handler. Only active in commit-mode. Detects whether
  // focus moved to one of the widget's OWN inputs (internal Tab hop
  // between DD ↔ MM ↔ YYYY) versus outside the widget entirely. Only
  // the latter is a semantic commit — internal hops are just typing.
  //
  // relatedTarget: the element receiving focus after the blur. Null
  // when focus leaves to the document body / a non-focusable element
  // — treated as a widget exit (commit fires). Any of the three own
  // refs → internal hop (skip). This runs on every blur regardless
  // of mode; the isCommitMode guard makes it a no-op for legacy
  // callers so the callback signature is one shape across both paths.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const next = e.relatedTarget as Node | null
    if (
      next === dayRef.current ||
      next === monthRef.current ||
      next === yearRef.current
    ) return
    setFocused(false)
    if (!isCommitMode || !onCommit) return
    const st = datePartsState(dd, mm, yyyy, allowFuture)
    // Half-typed / impossible / future on a past-only field → never committed
    // (the stored date stays; the field shows why).
    if (st.kind === 'invalid' || st.kind === 'future') return
    const committed = st.kind === 'valid' ? st.iso : null
    // No change → no commit (tabbing through a field shouldn't write) —
    // including the same date stored in another shape ("12/03/2025"), which
    // must not be rewritten just because it was shown.
    if (committed === value || (committed !== null && committed === textDateToIso(value))) return
    // Untouched boxes over a stored value this widget can't display (a
    // legacy / imported non-ISO string) → leave it alone rather than
    // silently nulling it on a mere tab-through.
    if (committed === null && dd === '' && mm === '' && yyyy === '' && value !== null && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return
    onCommit(committed)
  }

  // Each box composes a base style with the caller's fieldClassName
  // appended LAST so Tailwind's later-wins handles the error-border
  // swap (border-red-300 wins over border-casevo-border, etc.). The
  // base intentionally has no w-* — each box owns its own width.
  // min-w-0: an input's automatic flex minimum is its full w-*, so the three
  // boxes could never shrink and the group overflowed (over a neighbouring
  // field) in any container narrower than ~236px. Now they shrink to fit.
  const boxBase = 'min-w-0 border rounded px-2 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid text-center'
  const partsKind = showingStored ? 'stored' : datePartsState(dd, mm, yyyy, allowFuture).kind
  // A complete future date is flagged at once; an unfinished one once focus leaves.
  const message = partsKind === 'future' ? FUTURE_DATE_MESSAGE : partsKind === 'invalid' && !focused ? FINISH_DATE_MESSAGE : null
  const boxCls = (w: string): string => `${boxBase} ${w} ${fieldClassName ?? 'border-casevo-border'}${message ? ' !border-casevo-danger' : ''}`
  const errId = React.useId()
  const a11y = {
    'aria-invalid': !!message || !!invalid || undefined,
    'aria-describedby': [message ? errId : null, describedBy ?? null].filter(Boolean).join(' ') || undefined,
    onFocus: () => setFocused(true),
  }

  return (
    <>
    <div className={`flex items-center gap-2 ${className ?? ''}`} role="group" aria-label={ariaLabel ? `${ariaLabel} (DD/MM/YYYY)` : undefined}>
      <input
        ref={dayRef}
        type="text"
        inputMode="numeric"
        maxLength={2}
        placeholder="DD"
        value={dd}
        onChange={onDay}
        onBlur={handleBlur}
        aria-label={ariaLabel ? `${ariaLabel} — day` : 'Day'}
        {...a11y}
        className={boxCls('w-14')}
      />
      <span className="shrink-0 text-casevo-mid">/</span>
      <input
        ref={monthRef}
        type="text"
        inputMode="numeric"
        maxLength={2}
        placeholder="MM"
        value={mm}
        onChange={onMonth}
        onKeyDown={onMonthKey}
        onBlur={handleBlur}
        aria-label={ariaLabel ? `${ariaLabel} — month` : 'Month'}
        {...a11y}
        className={boxCls('w-14')}
      />
      <span className="shrink-0 text-casevo-mid">/</span>
      <input
        ref={yearRef}
        type="text"
        inputMode="numeric"
        maxLength={4}
        placeholder="YYYY"
        value={yyyy}
        onChange={onYear}
        onKeyDown={onYearKey}
        onBlur={handleBlur}
        aria-label={ariaLabel ? `${ariaLabel} — year` : 'Year'}
        {...a11y}
        className={boxCls('w-20')}
      />
    </div>
    {message && <span id={errId} role="alert" className="block text-[12px] text-casevo-danger mt-[5px]">{message}</span>}
    </>
  )
}

export default DateInput
